import { writable, derived, get } from 'svelte/store';

// Type for the privacy settings
// This is just for documentation, as we're using JS
/**
 * @typedef {Object} PrivacySettings
 * @property {boolean} privacyAccepted - Whether privacy policy is accepted
 * @property {boolean} analyticsAccepted - Whether analytics is accepted
 * @property {string|null} lastUpdated - ISO timestamp of last update
 */

// Define our privacy settings model with default values
const createPrivacyStore = () => {
  // Define the initial state with proper types
  /** @type {PrivacySettings} */
  const initialState = {
    privacyAccepted: false,
    analyticsAccepted: false,
    lastUpdated: null
  };

  const { subscribe, set, update } = writable(initialState);

  // Load settings from localStorage on initialization
  const initialize = () => {
    const savedSettings = localStorage.getItem('privacySettings');
    
    if (savedSettings) {
      try {
        /** @type {Partial<PrivacySettings>} */
        const parsedSettings = JSON.parse(savedSettings);
        
        // Update with saved settings, ensuring all fields exist
        update(state => ({
          ...state,
          ...parsedSettings,
        }));
        
        return true;
      } catch (e) {
        console.error('Failed to parse privacy settings:', e);
      }
    }
    
    return false;
  };

  // Save current settings to localStorage
  /**
   * @param {PrivacySettings} settings
   */
  const saveSettings = (settings) => {
    try {
      localStorage.setItem('privacySettings', JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save privacy settings:', e);
    }
  };

  return {
    subscribe,
    
    // Accept all privacy and analytics options
    acceptAll: () => {
      /** @type {PrivacySettings} */
      const newSettings = {
        privacyAccepted: true,
        analyticsAccepted: true,
        lastUpdated: new Date().toISOString()
      };
      
      set(newSettings);
      saveSettings(newSettings);
    },
    
    // Accept with custom options
    /**
     * @param {Partial<PrivacySettings>} options
     */
    acceptCustom: (options) => {
      update(state => {
        /** @type {PrivacySettings} */
        const newSettings = {
          ...state,
          ...options,
          lastUpdated: new Date().toISOString()
        };
        
        saveSettings(newSettings);
        return newSettings;
      });
    },
    
    // Reset all settings (for testing purposes)
    reset: () => {
      set(initialState);
      localStorage.removeItem('privacySettings');
    },
    
    // Initialize settings from localStorage
    initialize
  };
};

// Create and export the privacy store
export const privacyStore = createPrivacyStore();

// For backward compatibility with the old implementation
export const privacyAccepted = derived(
  privacyStore,
  $privacyStore => $privacyStore.privacyAccepted
);

// Derived store that tells us if analytics can be loaded
export const analyticsEnabled = derived(
  privacyStore,
  $privacyStore => $privacyStore.analyticsAccepted
);

// Helper to get current privacy settings
/**
 * @returns {PrivacySettings}
 */
export const getPrivacySettings = () => {
  return get(privacyStore);
}; 